import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { routes } from 'app/routing/routesConfig';
import { useAppDispatch, useAppSelector } from 'app/store';
import { subscriptionSubscribeButtonAction } from 'app/store/Mixpanel/slice';
import { getStorage } from 'app/store/Storage/selectors';
import { getDefaultCurrency } from 'app/store/Subscription/selectors';
import { getPrice } from 'app/utils/utils';
import { useLocation } from 'react-router-dom';
import { ReactComponent as LogoSymbol } from 'resources/images/symbol.svg';
import { PricingInterval, Tier } from 'types/subscription.types';
import { useSubscription } from 'app/components/Subscription/hooks';
import { CreateSubscriptionOverlay } from 'app/components/Subscription/CreateSubscriptionOverlay';
import { useCloseStorageBanner } from 'app/components/Storage/StorageBanner/hooks';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { bytesToGigabytes, getNextTier } from '../utils';

export interface StorageBannerComponentProps {
  currency?: string;
  nextTier: Tier;
  tierList: Tier[];
  onCloseStorageBanner: () => void;
}

export const StorageBannerComponent = ({
  tierList,
  nextTier,
  currency,
  onCloseStorageBanner,
}: StorageBannerComponentProps) => {
  const interval: PricingInterval = 'month';

  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getPriceLabel = () => {
    const pricePlan = nextTier.pricingPlans.find((plan) => {
      return plan.interval === interval && plan.currency === currency;
    });

    return `${getPrice(
      pricePlan?.price || 0,
      pricePlan?.currency
    )}/${interval}`;
  };

  return (
    <ContentContainer flex={0} pt={6}>
      <Flex bg="B.20" px={2} py={2} borderRadius={4} alignItems="center">
        <Icon
          width={9}
          height={9}
          data-testid="logo"
          as={LogoSymbol}
          color="M.70"
        />
        <Box flex={1} borderLeft="1px solid" borderColor="B.30" pl={2} ml="3px">
          <Text
            fontSize="sm"
            fontWeight={500}
            data-testid="storage-banner-title"
          >
            Keep Creating & Collaborating.
          </Text>
          <Text
            fontSize="xs"
            opacity={0.72}
            data-testid="storage-banner-message"
          >
            <Text as="span" fontSize="xs" data-testid="storage-banner-limit">
              {bytesToGigabytes(nextTier.limitBytes || 0)} GB of storage
            </Text>{' '}
            from only{' '}
            <Text as="span" fontSize="xs" data-testid="storage-banner-price">
              {getPriceLabel()}
            </Text>
            . Cancel anytime.
          </Text>
        </Box>
        <Button
          size="sm"
          py={1.5}
          px={3}
          fontSize="sm"
          onClick={() => {
            dispatch(subscriptionSubscribeButtonAction());
            onOpen();
          }}
        >
          Upgrade
        </Button>
        <IconButton
          aria-label="close"
          variant="text"
          size="xs"
          icon={<CloseIcon width={16} height={16} />}
          onClick={onCloseStorageBanner}
          ml={4}
          color="B.50"
          rounded="full"
          border="1px solid"
          borderColor="B.50"
        />
        <CreateSubscriptionOverlay
          tiers={tierList}
          open={isOpen}
          onClose={onClose}
        />
      </Flex>
    </ContentContainer>
  );
};

export const StorageBanner = () => {
  const whiteListedPaths = [routes.songs.route];

  const { onCloseStorageBanner, storageBannerClosed } = useCloseStorageBanner();
  const storage = useAppSelector(getStorage);
  const defaultCurrency = useAppSelector(getDefaultCurrency);
  const { tierList, subscription } = useSubscription();

  const { pathname } = useLocation();

  const nextTier = getNextTier(tierList, subscription);

  const hide =
    !storage ||
    storage.quotaStatus === 'UnderLimit' ||
    !nextTier ||
    !whiteListedPaths.some((path) => pathname.endsWith(path)) ||
    storageBannerClosed;

  if (hide) {
    return null;
  }

  return (
    <StorageBannerComponent
      nextTier={nextTier}
      tierList={tierList}
      currency={defaultCurrency}
      onCloseStorageBanner={onCloseStorageBanner}
    />
  );
};
